import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "@/hooks/use-toast";

import type { Tire } from "@/lib/inventory";

type CartItem = { 
  sku: string; 
  name: string; 
  price: number; 
  qty: number;
  stockLevel?: number;  // Track original stock level for low-stock alerts
  isLowStock?: boolean; // Flag for items with stock 4-7
  supplierId?: string;  // Track supplier for urgent order alerts
};

const KEY = "ktn-cart";

type CartContextType = {
  items: CartItem[];
  add: (tire: Tire) => void;
  remove: (sku: string) => void;
  update: (sku: string, qty: number) => void;
  total: number;
  clear: () => void;
};

const CartContext = createContext<CartContextType | null>(null);

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = localStorage.getItem(KEY);
      return raw ? (JSON.parse(raw) as CartItem[]) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  const add = (tire: Tire & { stockLevel?: number; isLowStock?: boolean }) => {
    setItems((prev) => {
      const exists = prev.find((i) => i.sku === tire.sku);
      const maxQty = Math.max(0, Number(tire.stock || 0));
      const isSpecialOrder = Boolean((tire as any).specialOrder || (tire as any).stockStatus === "special_order");
      
      // Allow special orders regardless of stock
      if (isSpecialOrder) {
        if (exists) {
          toast({ title: "Added to cart", description: `${tire.brand} ${tire.model} (${tire.size})` });
          return prev.map((i) => (i.sku === tire.sku ? { ...i, qty: i.qty + 1 } : i));
        }
        toast({ title: "Added to cart", description: `${tire.brand} ${tire.model} (${tire.size})` });
        return [...prev, { 
          sku: tire.sku, 
          name: `${tire.brand} ${tire.model} ${tire.size}`, 
          price: tire.price, 
          qty: 1,
          stockLevel: tire.stockLevel,
          isLowStock: false,
          supplierId: (tire as any).supplierId || (tire as any).supplier_id
        }];
      }
      
      if (maxQty === 0) {
        toast({ title: "Out of stock", description: "This item is currently unavailable." });
        return prev;
      }
      if (exists) {
        const newQty = Math.min(exists.qty + 1, maxQty);
        if (newQty === exists.qty) {
          toast({ title: "Stock limit", description: `Limited to ${maxQty} due to stock.` });
          return prev;
        }
        toast({ title: "Added to cart", description: `${tire.brand} ${tire.model} (${tire.size})` });
        return prev.map((i) => (i.sku === tire.sku ? { ...i, qty: newQty } : i));
      }
      toast({ title: "Added to cart", description: `${tire.brand} ${tire.model} (${tire.size})` });
      return [...prev, { 
        sku: tire.sku, 
        name: `${tire.brand} ${tire.model} ${tire.size}`, 
        price: tire.price, 
        qty: 1,
        stockLevel: tire.stockLevel,
        isLowStock: tire.isLowStock,
        supplierId: (tire as any).supplierId || (tire as any).supplier_id
      }];
    });
  };
  const remove = (sku: string) => setItems((prev) => prev.filter((i) => i.sku !== sku));
  const update = (sku: string, qty: number) => setItems((prev) => prev.map((i) => {
    if (i.sku !== sku) return i;
    // We cannot know per-item stock here without a lookup; keep clamp to [1..99]
    const clamped = Math.max(1, Math.min(qty, 99));
    return { ...i, qty: clamped };
  }));
  const clear = () => setItems([]);

  const total = useMemo(() => items.reduce((acc, i) => acc + i.price * i.qty, 0), [items]);

  const value = useMemo(() => ({ items, add, remove, update, total, clear }), [items, total]);
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
